// WindowActivationButtonDemo.jsx
import React, { useState, useEffect } from "react";
import WindowActivationButton from "../Shared/WindowActivationButton";
import { Container, Card, Form, Row, Col, ListGroup, Badge, Spinner, Alert, Button } from "react-bootstrap";
import axios from 'axios';
import { toast } from 'react-toastify';

const WindowActivationButtonDemo = () => {
  const [participantId, setParticipantId] = useState('');
  const [participantInfo, setParticipantInfo] = useState(null);
  const [availableWindows, setAvailableWindows] = useState([]);
  const [selectedWindows, setSelectedWindows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activate, setActivate] = useState(true);
  const [result, setResult] = useState(null);

  // Parse participant ID from URL if present
  useEffect(() => {
    const parseParticipantFromUrl = () => {
      const path = window.location.pathname;
      const regex = /\/participant\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
      const match = path.match(regex);
      
      if (match && match[1]) {
        setParticipantId(match[1]);
        fetchParticipantWindows(match[1]);
      }
    };
    
    parseParticipantFromUrl();
  }, []);

  const fetchParticipantWindows = async (id) => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      // Get participant info
      const infoResponse = await axios.get(`/api/participants/${id}`);
      setParticipantInfo(infoResponse.data);
      
      // Get available windows for activation
      const windowsResponse = await axios.get(`/api/participants/${id}/all-surveys-for-activation/`);
      setAvailableWindows(windowsResponse.data || []);
      setResult(null);
    } catch (error) {
      console.error('Error fetching participant data:', error);
      toast.error('Failed to fetch participant windows');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccess = (data) => {
    setResult({ success: true, data });
    // Refresh window data after activation/deactivation
    fetchParticipantWindows(participantId);
    toast.success(`Windows ${activate ? 'activated' : 'deactivated'} successfully`);
  };

  const handleError = (error) => {
    setResult({ success: false, error: error.message });
    console.error('Error:', error);
    toast.error(`Error ${activate ? 'activating' : 'deactivating'} windows`);
  };
  
  const toggleWindowSelection = (windowId) => {
    setSelectedWindows(prevSelected => {
      if (prevSelected.includes(windowId)) {
        return prevSelected.filter(id => id !== windowId);
      } else {
        return [...prevSelected, windowId];
      }
    });
  };
  
  const handleSelectAll = () => {
    if (selectedWindows.length === availableWindows.length) {
      setSelectedWindows([]);
    } else {
      setSelectedWindows(availableWindows.map(window => window.id));
    }
  };

  return (
    <Container className="mt-4 mb-5">
      <Card className="shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h4 className="mb-0">Survey Window Management</h4>
        </Card.Header>
        
        <Card.Body>
          <Row className="mb-4">
            <Col md={8}>
              <Form.Group>
                <Form.Label className="fw-bold">Participant UUID</Form.Label>
                <div className="d-flex">
                  <Form.Control 
                    type="text" 
                    value={participantId} 
                    onChange={(e) => setParticipantId(e.target.value)}
                    placeholder="Enter participant UUID..."
                    className="me-2"
                  />
                  <Button 
                    variant="primary" 
                    onClick={() => fetchParticipantWindows(participantId)}
                    disabled={!participantId || isLoading}
                  >
                    {isLoading ? (
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    ) : (
                      'Load'
                    )}
                  </Button>
                </div>
                <Form.Text className="text-muted">
                  Format: 8d96a329-6761-44c6-a6e4-fa0457ebdbcc
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Action</Form.Label>
                <div>
                  <Form.Check 
                    type="radio" 
                    label="Activate Windows" 
                    id="activate"
                    checked={activate} 
                    onChange={() => setActivate(true)}
                    inline
                  />
                  <Form.Check 
                    type="radio" 
                    label="Deactivate Windows" 
                    id="deactivate"
                    checked={!activate} 
                    onChange={() => setActivate(false)}
                    inline
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          
          {participantInfo && (
            <Alert variant="info" className="mb-4">
              <Row>
                <Col md={6}>
                  <strong>ID:</strong> {participantInfo.participant_id || 'N/A'}<br/>
                  <strong>Site:</strong> {participantInfo.site?.name || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>Subject:</strong> {participantInfo.subject_id || 'N/A'}<br/>
                  <strong>Study:</strong> {participantInfo.site?.study?.name || 'N/A'}
                </Col>
              </Row>
            </Alert>
          )}
          
          {isLoading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading participant data...</p>
            </div>
          ) : availableWindows.length > 0 ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Available Survey Windows</h5>
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={handleSelectAll}
                >
                  {selectedWindows.length === availableWindows.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <ListGroup className="mb-4">
                {availableWindows.map((window) => (
                  <ListGroup.Item 
                    key={window.id}
                    className="d-flex justify-content-between align-items-center"
                    action
                    active={selectedWindows.includes(window.id)}
                    onClick={() => toggleWindowSelection(window.id)}
                  >
                    <div>
                      <div className="fw-bold">{window.survey.name}</div>
                      <small className="text-muted">
                        Window: {new Date(window.window.start_time).toLocaleString()} - {new Date(window.window.end_time).toLocaleString()}
                      </small>
                    </div>
                    <div>
                      <Badge bg={window.window.status === 'open' ? 'success' : 'secondary'}>
                        {window.window.status}
                      </Badge>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
              
              <div className="d-flex justify-content-center mt-4">
                <WindowActivationButton 
                  participantId={participantId}
                  windowIds={selectedWindows}
                  activate={activate}
                  onSuccess={handleSuccess}
                  onError={handleError}
                  disabled={selectedWindows.length === 0}
                  variant={activate ? "success" : "danger"}
                  size="md"
                  className="px-4"
                  customLabel={`${activate ? 'Activate' : 'Deactivate'} Selected Windows (${selectedWindows.length})`}
                />
              </div>
            </>
          ) : participantId ? (
            <Alert variant="warning">
              No survey windows available for this participant. Please check if the participant has any assigned surveys.
            </Alert>
          ) : (
            <Alert variant="info">
              Enter a participant UUID and click "Load" to view available survey windows.
            </Alert>
          )}
          
          {result && (
            <Card className="mt-4">
              <Card.Header className={result.success ? "bg-success text-white" : "bg-danger text-white"}>
                Operation Result
              </Card.Header>
              <Card.Body>
                <pre className="mb-0" style={{ maxHeight: "200px", overflow: "auto" }}>
                  {JSON.stringify(result, null, 2)}
                </pre>
              </Card.Body>
            </Card>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default WindowActivationButtonDemo;